import type {
  AuthUser,
  ChatConversation,
  ChatMessage,
  Community,
  Consultation,
  DemandPost,
  DemandResponse,
  Expert,
  Listing,
  MyListing,
  PostItem,
  ProfileStats,
  PublicProfile,
  ServiceRequest,
  SocialPost,
  TaxonomySearchResult,
  LiveSession,
  MarketConfig,
  ServiceProvider,
  VideoComment,
  VideoItem,
} from "./types";

export const fallbackMarket: MarketConfig = {
  countryCode: "KE",
  countryName: "Kenya",
  currency: "KES",
  locale: "en-KE",
  regionLabel: "County",
  regionLabelPlural: "Counties",
  paymentLabel: "Mobile money",
};

export const demoTaxonomyResults: TaxonomySearchResult[] = [
  {
    id: "11111111-1111-4111-8111-111111111101",
    slug: "yellow-beans",
    name: "Yellow Beans",
    moduleScope: ["produce"],
    taxonomyPath: "Farm Produce > Legumes / Pulses > Beans > Yellow Beans",
    confidenceScore: 0.94,
    isFallback: true,
  },
  {
    id: "11111111-1111-4111-8111-111111111102",
    slug: "green-grams",
    name: "Green Grams",
    moduleScope: ["produce"],
    taxonomyPath: "Farm Produce > Legumes / Pulses > Green Grams",
    matchedAlias: "ndengu",
    confidenceScore: 0.92,
    isFallback: true,
  },
  {
    id: "11111111-1111-4111-8111-111111111103",
    slug: "tomatoes",
    name: "Tomatoes",
    moduleScope: ["produce"],
    taxonomyPath: "Farm Produce > Vegetables > Tomatoes",
    matchedAlias: "nyanya",
    confidenceScore: 0.9,
    isFallback: true,
  },
  {
    id: "11111111-1111-4111-8111-111111111104",
    slug: "irish-potatoes",
    name: "Irish Potatoes",
    moduleScope: ["produce"],
    taxonomyPath: "Farm Produce > Roots and Tubers > Irish Potatoes",
    matchedAlias: "waru",
    confidenceScore: 0.9,
    isFallback: true,
  },
  {
    id: "11111111-1111-4111-8111-111111111105",
    slug: "kalahari-red-goat",
    name: "Kalahari Red Goat",
    moduleScope: ["produce"],
    taxonomyPath: "Livestock > Goats > Kalahari Red Goat",
    confidenceScore: 0.88,
    isFallback: true,
  },
  {
    id: "11111111-1111-4111-8111-111111111106",
    slug: "fresh-milk",
    name: "Fresh Milk",
    moduleScope: ["produce"],
    taxonomyPath: "Animal Products > Milk > Fresh Milk",
    confidenceScore: 0.87,
    isFallback: true,
  },
  {
    id: "11111111-1111-4111-8111-111111111107",
    slug: "eggs",
    name: "Eggs",
    moduleScope: ["produce"],
    taxonomyPath: "Animal Products > Eggs",
    confidenceScore: 0.87,
    isFallback: true,
  },
  {
    id: "11111111-1111-4111-8111-111111111108",
    slug: "maize",
    name: "Maize",
    moduleScope: ["produce"],
    taxonomyPath: "Farm Produce > Cereals / Grains > Maize",
    matchedAlias: "mahindi",
    confidenceScore: 0.86,
    isFallback: true,
  },
];

export const demoDynamicForms = {
  produce: {
    attributes: [
      {
        id: "demo-attr-grade",
        taxonomyNodeId: "demo",
        attributeName: "Grade",
        attributeKey: "grade",
        attributeType: "select",
        isRequired: false,
        options: ["Grade 1", "Grade 2", "Table grade", "Export grade", "Unsorted"],
        helpText: "Quality grade or sorting level buyers should expect.",
      },
      {
        id: "demo-attr-packaging",
        taxonomyNodeId: "demo",
        attributeName: "Packaging",
        attributeKey: "packaging",
        attributeType: "select",
        isRequired: false,
        options: ["Loose", "Sack", "Crate", "Tray", "Carton"],
        helpText: "How the produce will be packed for pickup or delivery.",
      },
      {
        id: "demo-attr-harvest",
        taxonomyNodeId: "demo",
        attributeName: "Harvest date",
        attributeKey: "harvest_date",
        attributeType: "date",
        isRequired: false,
        options: [],
      },
      {
        id: "demo-attr-moisture",
        taxonomyNodeId: "demo",
        attributeName: "Moisture level",
        attributeKey: "moisture_level",
        attributeType: "text",
        isRequired: false,
        options: [],
        helpText: "Useful for cereals and pulses.",
      },
    ],
    suggestions: [
      { id: "demo-suggestion-buyers", displayLabel: "Find buyers", suggestedModule: "buyer_demand" },
      { id: "demo-suggestion-transport", displayLabel: "Book transport", suggestedModule: "transport" },
      { id: "demo-suggestion-storage", displayLabel: "Find storage", suggestedModule: "storage" },
    ],
  },
};

export const demoListings: Listing[] = [
  {
    id: "lst-yellow-beans",
    title: "Yellow beans, cleaned and sorted",
    category: "Legumes",
    taxonomyPath: "Farm Produce > Legumes / Pulses > Beans > Yellow Beans",
    description: "Freshly harvested yellow beans from a farmer group. Cleaned, sun-dried, and packed in sacks.",
    quantity: 420,
    unit: "kg",
    price: 170,
    currency: "KES",
    grade: "Grade 1",
    location: "Nanyuki",
    region: "Laikipia",
    countryCode: "KE",
    harvestDate: "2026-06-18",
    imageUrl: "https://images.unsplash.com/photo-1515543904379-3d757afe72e4?auto=format&fit=crop&w=900&q=80",
    sellerId: "provider-greenridge",
    sellerName: "Greenridge Farmers",
    sellerRole: "Cooperative seller",
    sellerRating: 4.8,
    postedAt: "Today",
  },
  {
    id: "lst-tomatoes",
    title: "Greenhouse tomatoes",
    category: "Vegetables",
    taxonomyPath: "Farm Produce > Vegetables > Tomatoes",
    description: "Firm greenhouse tomatoes available for restaurants and small retailers. Harvested on order.",
    quantity: 75,
    unit: "crates",
    price: 2400,
    currency: "KES",
    grade: "Table grade",
    location: "Naivasha",
    region: "Nakuru",
    countryCode: "KE",
    harvestDate: "2026-06-25",
    imageUrl: "https://images.unsplash.com/photo-1592841200221-a6898f307baa?auto=format&fit=crop&w=900&q=80",
    sellerId: "provider-riftfresh",
    sellerName: "Rift Fresh Farm",
    sellerRole: "Commercial farmer",
    sellerRating: 4.7,
    postedAt: "2 h",
  },
  {
    id: "lst-potatoes",
    title: "Shangi potatoes",
    category: "Roots and tubers",
    taxonomyPath: "Farm Produce > Roots and Tubers > Irish Potatoes > Shangi",
    description: "Medium-size Shangi potatoes suitable for chips and household retail.",
    quantity: 180,
    unit: "bags",
    price: 3600,
    currency: "KES",
    grade: "Medium",
    location: "Njabini",
    region: "Nyandarua",
    countryCode: "KE",
    harvestDate: "2026-06-21",
    imageUrl: "https://images.unsplash.com/photo-1518977676601-b53f82aba655?auto=format&fit=crop&w=900&q=80",
    sellerId: "provider-highland",
    sellerName: "Highland Roots",
    sellerRole: "Farmer",
    sellerRating: 4.5,
    postedAt: "Yesterday",
  },
  {
    id: "lst-avocado",
    title: "Hass avocado export grade",
    category: "Fruits",
    taxonomyPath: "Farm Produce > Fruits > Avocado > Hass",
    description: "Sorted Hass avocados with farm traceability. Cold-chain pickup available nearby.",
    quantity: 1200,
    unit: "pieces",
    price: 22,
    currency: "KES",
    grade: "Export grade",
    location: "Kandara",
    region: "Murang'a",
    countryCode: "KE",
    harvestDate: "2026-06-24",
    imageUrl: "https://images.unsplash.com/photo-1601039641847-7857b994d704?auto=format&fit=crop&w=900&q=80",
    sellerId: "provider-muranga",
    sellerName: "Murang'a Avocado Hub",
    sellerRole: "Aggregator",
    sellerRating: 4.9,
    postedAt: "3 h",
  },
];

export const demoDemandPosts: DemandPost[] = [
  {
    id: "need-potatoes-20kg",
    title: "Need 20 kg potatoes for shop stock",
    taxonomyPath: "Farm Produce > Roots and Tubers > Irish Potatoes",
    buyerName: "Maua Fresh Shop",
    buyerType: "Retail shop",
    quantity: 20,
    unit: "kg",
    maxPrice: 95,
    currency: "KES",
    location: "Thika",
    region: "Kiambu",
    neededBy: "Tomorrow",
    responses: 3,
  },
  {
    id: "need-eggs-hotel",
    title: "Hotel sourcing trays of eggs",
    taxonomyPath: "Animal Products > Eggs",
    buyerName: "Valley View Hotel",
    buyerType: "Hotel",
    quantity: 40,
    unit: "trays",
    maxPrice: 430,
    currency: "KES",
    location: "Nakuru",
    region: "Nakuru",
    neededBy: "Fri",
    responses: 8,
  },
  {
    id: "need-yellow-beans",
    title: "Bulk yellow beans for school supplier",
    taxonomyPath: "Farm Produce > Legumes / Pulses > Beans > Yellow Beans",
    buyerName: "EduMeals Procurement",
    buyerType: "Institutional buyer",
    quantity: 600,
    unit: "kg",
    maxPrice: 180,
    currency: "KES",
    location: "Eldoret",
    region: "Uasin Gishu",
    neededBy: "Next week",
    responses: 11,
  },
];

export const demoMyListings: MyListing[] = [
  {
    id: "lst-yellow-beans",
    title: "Yellow beans, cleaned and sorted",
    status: "active",
    price: 170,
    currency: "KES",
    unit: "kg",
    quantity: 420,
    imageUrl: "https://images.unsplash.com/photo-1515543904379-3d757afe72e4?auto=format&fit=crop&w=900&q=80",
    category: "Legumes",
    taxonomyPath: "Farm Produce > Legumes / Pulses > Beans > Yellow Beans",
    createdAt: "2026-06-20T08:00:00Z",
    updatedAt: "2026-06-27T08:00:00Z",
  },
  {
    id: "lst-tomatoes",
    title: "Greenhouse tomatoes",
    status: "active",
    price: 2400,
    currency: "KES",
    unit: "crates",
    quantity: 75,
    imageUrl: "https://images.unsplash.com/photo-1592841200221-a6898f307baa?auto=format&fit=crop&w=900&q=80",
    category: "Vegetables",
    taxonomyPath: "Farm Produce > Vegetables > Tomatoes",
    createdAt: "2026-06-25T10:30:00Z",
    updatedAt: "2026-06-26T07:15:00Z",
  },
];

export const demoServiceRequests: ServiceRequest[] = [
  {
    id: "req-transport-1",
    requestType: "transport",
    status: "pending",
    providerId: "provider-swift-haulage",
    providerName: "Swift Haulage",
    providerType: "transport",
    requesterId: "demo-user",
    requesterName: "Demo User",
    payload: { pickup: "Nanyuki", dropoff: "Nairobi CBD Market", cargoType: "Yellow beans", weightTons: 0.4 },
    estimate: 5200,
    pickupLabel: "Nanyuki",
    dropoffLabel: "Nairobi CBD Market",
    cargoLabel: "Yellow beans",
    createdAt: "2026-06-26T09:00:00Z",
    updatedAt: "2026-06-26T09:00:00Z",
  },
  {
    id: "req-storage-1",
    requestType: "storage",
    status: "accepted",
    providerId: "provider-coldstore",
    providerName: "Rift Cold Store",
    providerType: "storage",
    requesterId: "demo-user",
    requesterName: "Demo User",
    payload: { message: "Need cold storage for 75 crates of tomatoes for 5 days." },
    estimate: null,
    pickupLabel: null,
    dropoffLabel: null,
    cargoLabel: "Need cold storage for 75 crates of tomatoes for 5 days.",
    createdAt: "2026-06-24T14:00:00Z",
    updatedAt: "2026-06-25T08:00:00Z",
  },
];

export const demoConsultations: Consultation[] = [
  {
    id: "cons-1",
    requesterId: "demo-user",
    expertUserId: "expert-agnes",
    providerId: "provider-agnes",
    consultationType: "video",
    status: "accepted",
    scheduledAt: null,
    slot: "10:00 AM",
    durationMinutes: 30,
    message: "Tomato leaf curl in my greenhouse — need advice before spraying.",
    amount: 0,
    currency: "KES",
    acceptedAt: "2026-06-26T12:00:00Z",
    rejectedAt: null,
    rejectedReason: null,
    createdAt: "2026-06-26T09:30:00Z",
  },
];

export const demoDemandResponses: Record<string, DemandResponse[]> = {
  "need-yellow-beans": [
    {
      id: "resp-1",
      demandPostId: "need-yellow-beans",
      responderUserId: "provider-greenridge",
      message: "We have 420 kg cleaned and sorted, can deliver to Eldoret this week.",
      offeredPrice: 172,
      availableQuantity: 420,
      status: "pending",
      createdAt: "2026-06-27T07:45:00Z",
    },
    {
      id: "resp-2",
      demandPostId: "need-yellow-beans",
      responderUserId: "provider-highland",
      message: "Can supply 200 kg at your max price.",
      offeredPrice: 180,
      availableQuantity: 200,
      status: "pending",
      createdAt: "2026-06-26T16:20:00Z",
    },
  ],
};

export const demoPublicProfiles: PublicProfile[] = [
  {
    id: "provider-greenridge",
    displayName: "Greenridge Farmers",
    username: "greenridge",
    avatarUrl: null,
    coverUrl: null,
    primaryRole: "farmer",
    roles: ["farmer"],
    trustTier: 2,
    countryCode: "KE",
    isFollowing: false,
    createdAt: "2026-01-12T08:00:00.000Z",
    bio: "Farmer cooperative in Laikipia growing beans, potatoes, and vegetables with shared cold storage.",
    locationLabel: "Nanyuki, Laikipia",
    crops: ["yellow beans", "potatoes"],
    livestock: [],
    specialization: null,
    storageType: null,
    categories: [],
  },
];

export const demoProfileStats: Record<string, ProfileStats> = {
  greenridge: {
    followersCount: 214,
    followingCount: 31,
    postsCount: 18,
    listingsCount: 2,
    videosCount: 1,
    totalLikes: 460,
  },
};

export const demoSocialPosts: SocialPost[] = [
  {
    id: "demo-feed-rains",
    authorId: "provider-riftfresh",
    authorDisplayName: "Rift Fresh Farm",
    authorRole: "Farmer",
    text: "Rain has slowed picking today, but greenhouse tomato quality is still strong. Restaurants around Naivasha can book tomorrow morning harvest.",
    imageUrls: ["https://images.unsplash.com/photo-1592841200221-a6898f307baa?auto=format&fit=crop&w=900&q=80"],
    hashtags: ["tomatoes", "harvest", "market"],
    locationName: "Naivasha",
    likesCount: 34,
    commentsCount: 8,
    sharesCount: 2,
    createdAt: "2026-06-27T08:20:00.000Z",
  },
  {
    id: "demo-feed-buyer",
    authorId: "buyer-maua-fresh",
    authorDisplayName: "Maua Fresh Shop",
    authorRole: "Buyer",
    text: "Looking for consistent potato suppliers who can deliver small batches twice a week. Clean grading matters more than volume for us.",
    imageUrls: [],
    hashtags: ["buyers", "potatoes", "retail"],
    locationName: "Thika",
    likesCount: 21,
    commentsCount: 5,
    sharesCount: 1,
    createdAt: "2026-06-27T07:35:00.000Z",
  },
  {
    id: "demo-feed-storage",
    authorId: "provider-coldstore",
    authorDisplayName: "Rift Cold Storage",
    authorRole: "Storage provider",
    text: "Two cold-room slots opened up for avocado and leafy vegetables this weekend. Message us before dispatch so receiving is planned.",
    imageUrls: ["https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=900&q=80"],
    hashtags: ["coldchain", "storage", "avocado"],
    locationName: "Nakuru",
    likesCount: 19,
    commentsCount: 4,
    sharesCount: 3,
    createdAt: "2026-06-26T16:15:00.000Z",
  },
];

export const demoProviders: ServiceProvider[] = [
  {
    id: "provider-coldstore",
    name: "Rift Cold Storage",
    type: "storage",
    headline: "Short-term cold rooms for tomatoes, avocado, dairy, and leafy produce.",
    taxonomyPath: "Storage Services > Cold Storage > Short-Term Produce Cold Room",
    location: "Naivasha",
    region: "Nakuru",
    rating: 4.7,
    jobsCompleted: 182,
    rateLabel: "from KES 18 / crate / day",
    imageUrl: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=900&q=80",
    amenities: ["Cold room", "Loading bay", "Security", "Digital inventory"],
    availability: "Open today",
  },
  {
    id: "provider-transport",
    name: "Mkulima Produce Movers",
    type: "transport",
    headline: "Pickup and canter transport for vegetables, fruits, milk, and farm inputs.",
    taxonomyPath: "Transport Services > Produce Transport > Pickup / Small Truck",
    location: "Limuru",
    region: "Kiambu",
    rating: 4.6,
    jobsCompleted: 247,
    rateLabel: "quote by route",
    imageUrl: "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=900&q=80",
    amenities: ["Pickup trucks", "Loading help", "Same-day trips"],
    availability: "4 vehicles available",
  },
  {
    id: "provider-agrovet",
    name: "HarvestCare Agrovet",
    type: "agrovet",
    headline: "Certified farm inputs, crop protection products, and animal health supplies.",
    taxonomyPath: "Agrovet / Farm Inputs > Seeds / Fertilizers / Veterinary Medicine",
    location: "Karatina",
    region: "Nyeri",
    rating: 4.8,
    jobsCompleted: 391,
    rateLabel: "retail and bulk quotes",
    imageUrl: "https://images.unsplash.com/photo-1584263347416-85a696b4eda7?auto=format&fit=crop&w=900&q=80",
    amenities: ["Delivery", "Certified stock", "Agronomist support"],
    availability: "Closes 6 PM",
  },
  {
    id: "provider-processing",
    name: "Central Grain Cleaners",
    type: "processing",
    headline: "Cleaning, sorting, drying, and bagging for cereals and pulses.",
    taxonomyPath: "Processing Services > Cleaning and Sorting > Cereals and Pulses",
    location: "Embu",
    region: "Embu",
    rating: 4.5,
    jobsCompleted: 119,
    rateLabel: "from KES 3 / kg",
    imageUrl: "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?auto=format&fit=crop&w=900&q=80",
    amenities: ["Drying", "Sorting", "Packaging", "Quality checks"],
    availability: "Booking slots this week",
  },
];

export const demoExperts: Expert[] = [
  {
    id: "expert-vet-dairy",
    name: "Dr. Lydia Kamau",
    title: "Dairy cattle veterinary officer",
    taxonomyPath: "Farm Experts > Veterinary Services > Dairy Cattle Vet",
    location: "Kiambu",
    region: "Kiambu",
    rating: 4.9,
    consultations: 286,
    languages: ["English", "Swahili", "Kikuyu"],
    fee: 900,
    currency: "KES",
    imageUrl: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&w=900&q=80",
    specializations: ["Mastitis", "Vaccination", "AI referrals", "Emergency field visits"],
    videoSupported: true,
  },
  {
    id: "expert-tomato",
    name: "Peter Mwangi",
    title: "Tomato disease and greenhouse advisor",
    taxonomyPath: "Farm Experts > Crop Experts > Pest and Disease Experts > Tomato",
    location: "Naivasha",
    region: "Nakuru",
    rating: 4.8,
    consultations: 341,
    languages: ["English", "Swahili"],
    fee: 700,
    currency: "KES",
    imageUrl: "https://images.unsplash.com/photo-1581093458791-9d15482442f6?auto=format&fit=crop&w=900&q=80",
    specializations: ["Blight", "Greenhouse nutrition", "Spray programs"],
    videoSupported: true,
  },
];

export const demoLiveSessions: LiveSession[] = [
  {
    id: "live-tomato-blight",
    hostUserId: "expert-tomato",
    title: "Tomato blight control this wet season",
    description: "A practical expert class on spotting blight early, choosing safe spray intervals, and deciding when to call an advisor.",
    hostName: "Peter Mwangi",
    category: "Expert live class",
    taxonomyPath: "Skylight Live > Expert Training > Tomato Disease",
    startsAt: "Today 7:30 PM",
    status: "scheduled",
    viewers: 0,
    price: 0,
    currency: "KES",
    accessType: "public",
    sessionType: "expert_live_class",
    replayEnabled: true,
    commentsEnabled: true,
    speakerRequestsEnabled: true,
  },
  {
    id: "live-market-beans",
    hostUserId: "market-desk",
    title: "Bean buyers live market check",
    description: "Farmers and bulk buyers discuss yellow beans, green grams, and current demand in major town markets.",
    hostName: "Skylight Market Desk",
    category: "Market live",
    taxonomyPath: "Skylight Live > Market Live > Legumes",
    startsAt: "Live now",
    status: "live",
    viewers: 86,
    price: 0,
    currency: "KES",
    accessType: "public",
    sessionType: "market_live",
    audienceMode: "webrtc",
    commentsEnabled: true,
    speakerRequestsEnabled: true,
  },
];

export const demoVideos: VideoItem[] = [
  {
    id: "vid-tomato-pruning",
    title: "Tomato pruning in a small greenhouse",
    description: "A short practical walkthrough of removing lower leaves, tying stems, and keeping airflow clean.",
    authorId: "expert-tomato",
    authorDisplayName: "Peter Mwangi",
    authorRole: "Greenhouse advisor",
    thumbnailUrl: "https://images.unsplash.com/photo-1592841200221-a6898f307baa?auto=format&fit=crop&w=900&q=80",
    videoUrl: "https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4",
    durationSeconds: 74,
    category: "Greenhouse",
    hashtags: ["tomatoes", "greenhouse", "training"],
    isLearnContent: true,
    viewCount: 1840,
    likesCount: 216,
    commentsCount: 18,
    createdAt: "2026-06-26T13:00:00.000Z",
  },
  {
    id: "vid-beans-cleaning",
    title: "Cleaning yellow beans before sale",
    description: "How a cooperative sorts stones, moisture-checks sacks, and prepares beans for bulk buyers.",
    authorId: "provider-greenridge",
    authorDisplayName: "Greenridge Farmers",
    authorRole: "Cooperative seller",
    thumbnailUrl: "https://images.unsplash.com/photo-1515543904379-3d757afe72e4?auto=format&fit=crop&w=900&q=80",
    videoUrl: "https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4",
    durationSeconds: 58,
    category: "Post-harvest",
    hashtags: ["beans", "sorting", "market"],
    isLearnContent: true,
    viewCount: 1190,
    likesCount: 144,
    commentsCount: 11,
    createdAt: "2026-06-25T10:30:00.000Z",
  },
  {
    id: "vid-cold-storage-avocado",
    title: "Keeping avocado quality during storage",
    description: "A storage provider explains receiving, crate spacing, cooling, and dispatch checks.",
    authorId: "provider-coldstore",
    authorDisplayName: "Rift Cold Storage",
    authorRole: "Storage provider",
    thumbnailUrl: "https://images.unsplash.com/photo-1601039641847-7857b994d704?auto=format&fit=crop&w=900&q=80",
    videoUrl: "https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4",
    durationSeconds: 92,
    category: "Storage",
    hashtags: ["avocado", "cold-chain", "storage"],
    isLearnContent: true,
    viewCount: 930,
    likesCount: 99,
    commentsCount: 7,
    createdAt: "2026-06-24T09:45:00.000Z",
  },
];

export const demoCommunities: Community[] = [
  {
    id: "community-tomato-network",
    name: "Tomato Farmers Network",
    slug: "tomato-farmers-network",
    description: "Crop-based group for production tips, disease alerts, greenhouse practice, and buyer leads.",
    coverImageUrl: "https://images.unsplash.com/photo-1592841200221-a6898f307baa?auto=format&fit=crop&w=1200&q=80",
    communityType: "open",
    category: "Crop-based group",
    county: "Nakuru",
    countryIso2: "KE",
    tags: ["tomatoes", "greenhouse", "disease-alerts"],
    memberCount: 1240,
    postCount: 86,
    isFeatured: true,
    ownerId: "expert-tomato",
    createdAt: "2026-06-12T08:00:00.000Z",
  },
  {
    id: "community-dairy-circle",
    name: "Dairy Farmers Circle",
    slug: "dairy-farmers-circle",
    description: "Livestock group for feed, vet referrals, milk buyer coordination, and farmer training.",
    coverImageUrl: "https://images.unsplash.com/photo-1527153857715-3908f2bae5e8?auto=format&fit=crop&w=1200&q=80",
    communityType: "closed",
    category: "Livestock group",
    county: "Kiambu",
    countryIso2: "KE",
    tags: ["dairy", "milk", "veterinary"],
    memberCount: 860,
    postCount: 52,
    isFeatured: true,
    ownerId: "expert-vet-dairy",
    createdAt: "2026-06-10T11:30:00.000Z",
  },
  {
    id: "community-young-agropreneurs",
    name: "Young Agropreneurs",
    slug: "young-agropreneurs",
    description: "Business-focused community for new farmers, service providers, processors, and agribusiness builders.",
    coverImageUrl: "https://images.unsplash.com/photo-1530836369250-ef72a3f5cda8?auto=format&fit=crop&w=1200&q=80",
    communityType: "open",
    category: "Agribusiness group",
    county: "Nairobi",
    countryIso2: "KE",
    tags: ["agribusiness", "youth", "markets"],
    memberCount: 2110,
    postCount: 131,
    isFeatured: false,
    ownerId: "market-desk",
    createdAt: "2026-06-02T09:15:00.000Z",
  },
];

export const demoCommunityPosts: Record<string, PostItem[]> = {
  "community-tomato-network": [
    {
      id: "post-tomato-network-1",
      authorId: "expert-tomato",
      authorDisplayName: "Peter Mwangi",
      authorRole: "Greenhouse advisor",
      text: "Early blight pressure is rising in cooler zones. Check lower leaves twice a week and avoid overhead watering where possible.",
      imageUrls: [],
      hashtags: ["tomatoes", "blight", "greenhouse"],
      locationName: "Naivasha",
      likesCount: 38,
      commentsCount: 9,
      sharesCount: 3,
      communityId: "community-tomato-network",
      postType: "community",
      createdAt: "2026-06-27T09:30:00.000Z",
    },
    {
      id: "post-tomato-network-2",
      authorId: "provider-riftfresh",
      authorDisplayName: "Rift Fresh Farm",
      authorRole: "Commercial farmer",
      text: "We are comparing crate prices for next week. Anyone around Nakuru using reusable crates with good return discipline?",
      imageUrls: [],
      hashtags: ["packaging", "market"],
      locationName: "Nakuru",
      likesCount: 21,
      commentsCount: 6,
      sharesCount: 1,
      communityId: "community-tomato-network",
      postType: "community",
      createdAt: "2026-06-26T16:10:00.000Z",
    },
  ],
  "community-dairy-circle": [
    {
      id: "post-dairy-circle-1",
      authorId: "expert-vet-dairy",
      authorDisplayName: "Dr. Lydia Kamau",
      authorRole: "Dairy cattle veterinary officer",
      text: "Reminder: mastitis cases are easier to control when farmers report early swelling and milk changes before self-treatment.",
      imageUrls: [],
      hashtags: ["dairy", "mastitis", "vet"],
      locationName: "Kiambu",
      likesCount: 45,
      commentsCount: 12,
      sharesCount: 5,
      communityId: "community-dairy-circle",
      postType: "community",
      createdAt: "2026-06-27T07:45:00.000Z",
    },
  ],
  "community-young-agropreneurs": [
    {
      id: "post-agropreneurs-1",
      authorId: "market-desk",
      authorDisplayName: "Skylight Market Desk",
      authorRole: "Market analyst",
      text: "Best-performing pages this week: clean produce photos, clear units, and one direct contact route. Keep listings simple and structured.",
      imageUrls: [],
      hashtags: ["selling", "marketplace", "growth"],
      locationName: "Nairobi",
      likesCount: 67,
      commentsCount: 18,
      sharesCount: 9,
      communityId: "community-young-agropreneurs",
      postType: "community",
      createdAt: "2026-06-25T12:20:00.000Z",
    },
  ],
};

export const demoVideoComments: Record<string, VideoComment[]> = {
  "vid-tomato-pruning": [
    {
      id: "demo-video-comment-1",
      videoId: "vid-tomato-pruning",
      authorDisplayName: "Grace Wanjiru",
      text: "This is the exact pruning mistake I was making. Very helpful.",
      likesCount: 4,
      createdAt: "2026-06-26T15:10:00.000Z",
    },
    {
      id: "demo-video-comment-2",
      videoId: "vid-tomato-pruning",
      authorDisplayName: "Rift Fresh Farm",
      text: "Please do one on blight spray timing next.",
      likesCount: 2,
      createdAt: "2026-06-26T15:40:00.000Z",
    },
  ],
};

export const demoUser: AuthUser = {
  id: "demo-web-user",
  displayName: "Grace Wanjiru",
  email: "grace@demo.skylight.local",
  primaryRole: "buyer",
  roles: ["buyer", "farmer"],
};

export const demoConversations: ChatConversation[] = [
  {
    id: "demo-conv-yellow-beans",
    participantIds: ["demo-web-user", "provider-greenridge"],
    type: "direct",
    lastMessage: {
      text: "Yes, we can reserve 120 kg and arrange pickup tomorrow morning.",
      type: "text",
      senderId: "provider-greenridge",
      senderName: "Greenridge Farmers",
      sentAt: "2026-06-27T08:40:00.000Z",
    },
    unreadCount: 1,
    isArchived: false,
    updatedAt: "2026-06-27T08:40:00.000Z",
    otherUserDisplayName: "Greenridge Farmers",
    otherUserRole: "Cooperative seller",
  },
  {
    id: "demo-conv-coldstore",
    participantIds: ["demo-web-user", "provider-coldstore"],
    type: "direct",
    lastMessage: {
      text: "We have a 2-ton cold room slot from Friday.",
      type: "text",
      senderId: "provider-coldstore",
      senderName: "Rift Cold Storage",
      sentAt: "2026-06-26T16:15:00.000Z",
    },
    unreadCount: 0,
    isArchived: false,
    updatedAt: "2026-06-26T16:15:00.000Z",
    otherUserDisplayName: "Rift Cold Storage",
    otherUserRole: "Storage provider",
  },
];

export const demoMessages: Record<string, ChatMessage[]> = {
  "demo-conv-yellow-beans": [
    {
      id: "msg-demo-1",
      conversationId: "demo-conv-yellow-beans",
      senderId: "demo-web-user",
      senderDisplayName: "Grace Wanjiru",
      type: "text",
      text: "Hi, I am interested in the yellow beans listing. Is 120 kg still available?",
      isDeleted: false,
      createdAt: "2026-06-27T08:34:00.000Z",
    },
    {
      id: "msg-demo-2",
      conversationId: "demo-conv-yellow-beans",
      senderId: "provider-greenridge",
      senderDisplayName: "Greenridge Farmers",
      type: "text",
      text: "Yes, we can reserve 120 kg and arrange pickup tomorrow morning.",
      isDeleted: false,
      createdAt: "2026-06-27T08:40:00.000Z",
    },
  ],
  "demo-conv-coldstore": [
    {
      id: "msg-demo-3",
      conversationId: "demo-conv-coldstore",
      senderId: "demo-web-user",
      senderDisplayName: "Grace Wanjiru",
      type: "text",
      text: "Do you have cold storage available for avocado this weekend?",
      isDeleted: false,
      createdAt: "2026-06-26T15:50:00.000Z",
    },
    {
      id: "msg-demo-4",
      conversationId: "demo-conv-coldstore",
      senderId: "provider-coldstore",
      senderDisplayName: "Rift Cold Storage",
      type: "text",
      text: "We have a 2-ton cold room slot from Friday.",
      isDeleted: false,
      createdAt: "2026-06-26T16:15:00.000Z",
    },
  ],
};
